import axios, { type AxiosResponse, AxiosError } from 'axios'
import { ElMessage } from 'element-plus'

// 定义基础返回类型
export interface BaseResponse<T = never> {
  code: number
  msg: string
  data: T
}

// 创建Axios实例
const service = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL + 'api', // 从环境变量获取
  timeout: 60 * 1000,
  headers: { 'Content-Type': 'application/json;charset=utf-8' },
})

// 请求拦截器
service.interceptors.request.use(
  (config) => {
    // 此处可添加token等通用请求头
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.access_token = token
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  },
)

// 响应拦截器
service.interceptors.response.use(
  (response): Promise<AxiosResponse<BaseResponse>> => {
    const res = response.data as BaseResponse

    //如果返回是文件类型 responseType=blob
    if (response.config.responseType == 'blob') {
      return Promise.resolve(response)
    }

    // 根据业务状态码处理
    if (res.code !== 200) {
      // 错误处理逻辑
      handleResponseCode(res.code, res.msg)
      return Promise.reject(new Error(res.msg || 'Error'))
    }

    return Promise.resolve(response)
  },
  (error) => {
    // HTTP状态码处理
    const errorMessage = handleHttpError(error)
    return Promise.reject(new Error(errorMessage))
  },
)

// 处理业务状态码
function handleResponseCode(code: number, msg: string): void {
  // 根据状态码执行不同操作
  switch (code) {
    case 401:
      // 跳转登录页
      localStorage.clear()
      location.href = '/login'
      break
    case 403:
      ElMessage.error('无权限访问')
      break
    // ...其他状态码处理
    default:
      ElMessage.error(`${msg}`)
      break
  }
}

// 处理HTTP错误
function handleHttpError(error: AxiosError): string {
  let message = '请求失败'
  if (error.response) {
    switch (error.response.status) {
      case 400:
        message = '请求参数错误'
        break
      case 404:
        message = '资源未找到'
        break
      case 500:
        message = '服务器错误'
        break
      // 其他状态码处理...
    }
  } else if (error.request) {
    message = '服务器未响应'
  } else {
    message = error.message || '未知错误'
  }
  ElMessage.error(message)
  return message
}

export default service
